import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// note-client-mod -- NotebookManager
//
// 笔记本管理弹窗：创建 / 重命名 / 删除笔记本。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { radius, spacing, typography } from '../styles/theme.js';
import { inputBase, buttonBase } from '../styles/animations.js';
import { CloseIcon, PlusIcon, EditIcon, TrashIcon } from './icons.js';
import { useNotebookMutations } from '../hooks/use-notebook-mutations.js';
export function NotebookManager({ notebooks, onClose, onChanged }) {
    const { theme } = useTheme();
    const [name, setName] = React.useState('');
    const [editingId, setEditingId] = React.useState(null);
    const [editName, setEditName] = React.useState('');
    const [deleteId, setDeleteId] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const { mutating, error: mutationError, createNotebook, updateNotebook, deleteNotebook } = useNotebookMutations();
    const handleCreate = async () => {
        if (!name.trim()) {
            setError('请输入笔记本名称');
            return;
        }
        setBusy(true);
        setError(null);
        const result = await createNotebook({ name: name.trim() });
        setBusy(false);
        if (result) {
            setName('');
            onChanged();
        }
        else if (mutationError) {
            setError(mutationError);
        }
    };
    const handleStartEdit = (nb) => {
        setEditingId(nb._id);
        setEditName(nb.name);
    };
    const handleSaveEdit = async () => {
        if (!editingId || !editName.trim())
            return;
        setBusy(true);
        setError(null);
        const result = await updateNotebook(editingId, { name: editName.trim() });
        setBusy(false);
        if (result) {
            setEditingId(null);
            onChanged();
        }
        else if (mutationError) {
            setError(mutationError);
        }
    };
    const handleDelete = async () => {
        if (!deleteId)
            return;
        setBusy(true);
        setError(null);
        const ok = await deleteNotebook(deleteId);
        setBusy(false);
        if (ok) {
            setDeleteId(null);
            onChanged();
        }
        else if (mutationError) {
            setError(mutationError);
        }
    };
    const overlay = {
        position: 'fixed',
        inset: 0,
        backgroundColor: theme.bg.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    };
    const box = {
        backgroundColor: theme.bg.elevated,
        borderRadius: radius.xl,
        padding: spacing.xl,
        width: '440px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: theme.shadow.xl,
        animation: 'note-scale-in 0.2s ease forwards',
    };
    const input = inputBase(theme);
    const row = (nb) => {
        const isDefault = nb.isDefault;
        return (_jsx("div", { style: {
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                padding: `${spacing.sm} ${spacing.md}`,
                borderRadius: radius.md,
                backgroundColor: theme.bg.tertiary,
            }, children: editingId === nb._id ? (_jsxs(_Fragment, { children: [_jsx("input", { value: editName, onChange: (e) => setEditName(e.target.value), style: { ...input, flex: 1 }, autoFocus: true }), _jsx("button", { onClick: handleSaveEdit, style: buttonBase({ color: theme.status.primary }), title: "\u4FDD\u5B58", children: "\u2705" }), _jsx("button", { onClick: () => setEditingId(null), style: buttonBase({ color: theme.text.tertiary }), title: "\u53D6\u6D88", children: "\u2715" })] })) : (_jsxs(_Fragment, { children: [_jsxs("span", { style: { flex: 1, color: theme.text.primary, fontSize: typography.fontSize.base, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: [nb.name, isDefault && (_jsx("span", { style: { marginLeft: spacing.sm, fontSize: typography.fontSize.xs, color: theme.status.primary }, children: "\u9ED8\u8BA4" }))] }), _jsx("span", { style: { fontSize: typography.fontSize.xs, color: theme.text.tertiary }, children: nb.noteCount }), !isDefault && (_jsx("button", { onClick: () => handleStartEdit(nb), style: buttonBase({ color: theme.text.secondary }), title: "\u91CD\u547D\u540D", children: _jsx(EditIcon, {}) })), !isDefault && (_jsx("button", { onClick: () => setDeleteId(nb._id), style: buttonBase({ color: theme.status.danger }), title: "\u5220\u9664", children: _jsx(TrashIcon, {}) }))] })) }, nb._id));
    };
    return (_jsx("div", { style: overlay, onClick: onClose, children: _jsxs("div", { style: box, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.lg }, children: [_jsx("h3", { style: { margin: 0, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, color: theme.text.primary }, children: "\u7BA1\u7406\u7B14\u8BB0\u672C" }), _jsx("button", { onClick: onClose, style: buttonBase({ color: theme.text.tertiary }), title: "\u5173\u95ED", children: _jsx(CloseIcon, {}) })] }), _jsxs("div", { style: { display: 'flex', gap: spacing.sm, marginBottom: spacing.lg }, children: [_jsx("input", { placeholder: "\u65B0\u7B14\u8BB0\u672C\u540D\u79F0", value: name, onChange: (e) => setName(e.target.value), onKeyDown: (e) => { if (e.key === 'Enter')
                                handleCreate(); }, style: { ...input, flex: 1 } }), _jsxs("button", { onClick: handleCreate, disabled: busy || mutating, style: buttonBase({
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: spacing.xs,
                                padding: '8px 14px',
                                borderRadius: radius.md,
                                backgroundColor: theme.status.primary,
                                color: theme.text.inverse,
                                fontSize: typography.fontSize.sm,
                                fontWeight: typography.fontWeight.medium,
                            }), children: [_jsx(PlusIcon, {}), "\u65B0\u5EFA"] })] }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: spacing.sm }, children: notebooks.map(row) }), error && (_jsx("div", { style: { marginTop: spacing.md, padding: spacing.sm, borderRadius: radius.md, backgroundColor: theme.status.dangerBg, color: theme.status.danger, fontSize: typography.fontSize.base }, children: error })), _jsx("p", { style: { margin: `${spacing.md} 0 0`, fontSize: typography.fontSize.xs, color: theme.text.tertiary, lineHeight: typography.lineHeight.normal }, children: "\u5220\u9664\u7B14\u8BB0\u672C\u65F6\uFF0C\u5176\u4E2D\u7684\u7B14\u8BB0\u4F1A\u81EA\u52A8\u8FC1\u79FB\u5230\u9ED8\u8BA4\u7B14\u8BB0\u672C\u3002" }), deleteId && (_jsxs("div", { style: { marginTop: spacing.md, padding: spacing.md, borderRadius: radius.md, backgroundColor: theme.status.dangerBg }, children: [_jsx("p", { style: { margin: 0, fontSize: typography.fontSize.base, color: theme.status.danger }, children: "\u786E\u5B9A\u5220\u9664\u8BE5\u7B14\u8BB0\u672C\uFF1F\u5176\u4E2D\u7684\u7B14\u8BB0\u4F1A\u8FC1\u79FB\u5230\u9ED8\u8BA4\u7B14\u8BB0\u672C\u3002" }), _jsxs("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.sm }, children: [_jsx("button", { onClick: () => setDeleteId(null), style: buttonBase({ color: theme.text.secondary, fontSize: typography.fontSize.sm }), children: "\u53D6\u6D88" }), _jsx("button", { onClick: handleDelete, disabled: busy || mutating, style: buttonBase({ color: theme.status.danger, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium }), children: busy ? '删除中...' : '确认删除' })] })] }))] }) }));
}
//# sourceMappingURL=notebook-manager.js.map